// SPDX-FileCopyrightText: Copyright (c) 2025 Software GmbH, Darmstadt, Germany and/or its subsidiaries and/or its affiliates
// SPDX-FileContributor: Zead Alshukairi (planen-bauen 4.0 GmbH)
//
// SPDX-License-Identifier: Apache-2.0

import { Injectable } from '@angular/core';
import { InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  readonly supported = ['en', 'de'] as const;
  private langLoaded = false;

  constructor(private translate: TranslateService) {}

  init(preferredLanguage?: 'en' | 'de'): void {
    this.translate.addLangs([...this.supported]);
    this.translate.setDefaultLang('de');

    const saved = localStorage.getItem('lang');
    const browser = this.translate.getBrowserLang();
    const savedLanguage = saved === 'en' || saved === 'de' ? saved : null;
    const initial = preferredLanguage ?? savedLanguage ?? (browser === 'en' ? 'en' : 'de');

    this.translate.use(initial).subscribe(() => {
      this.langLoaded = true;
    });
    localStorage.setItem('lang', initial);
  }

  getAvailableLanguages(): readonly string[] {
    return this.supported;
  }

 /**
 * Changes the current language.
 *
 * @param lang the language code to use
 * @returns an Observable that completes when the language is changed
 */
  use(lang: string): Observable<InterpolatableTranslationObject> {
    this.langLoaded = false;
    localStorage.setItem('lang', lang);
    return this.translate.use(lang).pipe(
      map((res) => {
        this.langLoaded = true;
        return res;
      })
    );
  }

 /**
 * Gets the current language code.
 *
 * @returns the current language code
 */
  current(): string {
    return this.translate.currentLang || this.translate.defaultLang || 'de';
  }

 /**
 * Checks if the language translations are loaded.
 *
 * @returns true if loaded, false otherwise
 */
  isLangLoaded(): boolean {
    return this.langLoaded;
  }
}
